import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "/logo.png";
const HERO_MARBLE = "/hero-granipal.jpg";
const SESSION_KEY = "granipal_intro_played";

// Module-level guard: prevents StrictMode/double-mount races from spawning parallel timer chains.
let introScheduled = false;

/**
 * Apple-style opening logo animation with seamless transition to homepage.
 * - Stage 1: Logo fades in, gentle scale up
 * - Stage 2: Logo holds in center
 * - Stage 3: Dark vignette + glow fade out FIRST (reveals marble)
 * - Stage 4: Logo scales down + fades out gently (revealing hero text underneath)
 *   The marble background is identical to the hero, so the transition is invisible.
 */
export default function IntroLogoAnimation({ onComplete }) {
  // Initialize show state from sessionStorage immediately to avoid double-mount races
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(SESSION_KEY) !== '1';
  });
  const [stage, setStage] = useState('init');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const alreadyPlayed = window.sessionStorage.getItem(SESSION_KEY);
    if (alreadyPlayed) {
      window.dispatchEvent(new CustomEvent('granipal-intro-done'));
      if (onComplete) onComplete();
      return;
    }
    // Use module-level guard so StrictMode double-mount doesn't spawn two timer chains.
    if (introScheduled) return;
    introScheduled = true;
    // Timing:
    //  0.0s   logo fades in
    //  0.8s   hold
    //  2.0s   reveal: vignette + glow fade out
    //  2.6s   exit: logo fades + scales down, hero animations begin (crossfade)
    //  4.0s   AnimatePresence exit fully complete, overlay removed
    const t1 = setTimeout(() => setStage('hold'), 800);
    const t2 = setTimeout(() => setStage('reveal'), 2000);
    const t3 = setTimeout(() => {
      setStage('exit');
      window.dispatchEvent(new CustomEvent('granipal-intro-done'));
    }, 2600);
    const t4 = setTimeout(() => {
      window.sessionStorage.setItem(SESSION_KEY, '1');
      setShow(false);
      if (onComplete) onComplete();
    }, 4000);
    // No cleanup: in StrictMode, cleanup would clear the only valid timer chain
    // (the module-level guard prevents duplicates). Letting them fire is safe because
    // the component is mounted at the root and persists throughout the session.
  }, [onComplete]);

  const overlayFading = stage === 'reveal' || stage === 'exit';
  const logoFading = stage === 'exit';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none overflow-hidden"
          data-testid="intro-logo-overlay"
        >
          {/* Marbre Calacatta — fond identique au hero (même scale + position) pour transition invisible */}
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center scale-[1.06]"
            style={{
              backgroundImage: `url(${HERO_MARBLE})`,
            }}
          />
          {/* Vignette sombre — disparaît en premier pour révéler la matière */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 1 }}
            animate={{ opacity: overlayFading ? 0 : 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{
              background: 'radial-gradient(ellipse at center, rgba(20, 11, 7, 0.25) 0%, rgba(20, 11, 7, 0.55) 70%, rgba(20, 11, 7, 0.75) 100%)',
            }}
          />
          {/* Subtle gold radial glow */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: overlayFading ? 0 : 0.35,
              scale: overlayFading ? 1.35 : 1,
            }}
            transition={{ duration: overlayFading ? 1.4 : 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.04) 35%, transparent 70%)',
              filter: 'blur(20px)',
            }}
          />
          {/* Logo — fades + scales down in last stage (handing over visual focus to hero text) */}
          <motion.img
            src={LOGO_URL}
            alt="Granipal"
            initial={{ opacity: 0, scale: 0.92, filter: 'blur(8px)' }}
            animate={{
              opacity: logoFading ? 0 : 1,
              scale: logoFading ? 0.78 : 1,
              filter: 'blur(0px)',
              y: logoFading ? -20 : 0,
            }}
            transition={{
              opacity: { duration: logoFading ? 1.0 : 1.0, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
              y: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
            }}
            className="h-24 sm:h-32 lg:h-40 w-auto object-contain relative z-10"
            data-testid="intro-logo-image"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
